import { useState, useEffect } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, X, CreditCard } from "lucide-react";
import { COMPANY, NAV_LINKS } from "@/lib/site";

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-50 backdrop-blur-xl border-b transition-colors ${
        scrolled ? "bg-white/85 border-[#E5E1DE]" : "bg-white/70 border-transparent"
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-24">
          <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3">
            <img src={COMPANY.logo} alt={COMPANY.name} className="h-16 w-16 md:h-[72px] md:w-[72px] object-contain" />
            <span className="hidden sm:block font-heading font-black text-xl tracking-tight text-[#1A110B] leading-none">
              BETA-TECH<span className="block text-[12px] font-medium tracking-[0.2em] text-[#8B4513]">SYSTEMS, INC.</span>
            </span>
          </Link>

          <nav className="hidden md:flex items-center gap-9">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `text-sm font-semibold tracking-wide transition-colors ${
                    isActive ? "text-[#00B4C4]" : "text-[#1A110B] hover:text-[#00B4C4]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              href={COMPANY.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="nav-payment-btn"
              className="hidden sm:inline-flex items-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-xs px-5 py-2.5 rounded-sm hover:-translate-y-0.5 hover:shadow-lg transition-all"
            >
              <CreditCard className="h-4 w-4" /> Make a Payment
            </a>
            <button
              data-testid="mobile-menu-toggle"
              className="md:hidden text-[#1A110B]"
              onClick={() => setOpen((v) => !v)}
              aria-label="Toggle menu"
            >
              {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>

      {open && (
        <div data-testid="mobile-menu" className="md:hidden border-t border-[#E5E1DE] bg-white">
          <nav className="flex flex-col px-6 py-4 gap-1">
            {NAV_LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                onClick={() => setOpen(false)}
                data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                className={({ isActive }) =>
                  `py-3 text-base font-semibold border-b border-[#F0EDEA] ${
                    isActive ? "text-[#00B4C4]" : "text-[#1A110B]"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <a
              href={COMPANY.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="mobile-payment-btn"
              className="mt-3 inline-flex items-center justify-center gap-2 bg-[#FFA500] text-[#1A110B] font-heading font-black tracking-wider uppercase text-sm px-5 py-3 rounded-sm"
            >
              <CreditCard className="h-4 w-4" /> Make a Payment
            </a>
          </nav>
        </div>
      )}
    </header>
  );
}
